import React, { CSSProperties, useContext, useState } from 'react'
import RtcContext from './RtcContext'
import { IRemoteVideoTrack } from 'agora-rtc-react'
import RemoteVideoMute from './Controls/Remote/RemoteVideoMute'
import RemoteAudioMute from './Controls/Remote/RemoteAudioMute'
import PropsContext, { UIKitUser } from './PropsContext'
import VideoPlaceholder from './VideoPlaceholder'
import Username from './Username'
import Badge from './Controls/Badge'
import FittedVideo, { VideoLayout } from './FittedVideo'
/**
 * React component to display the user video in maximized view
 */
const MaxVideoView = (props: {
  user: UIKitUser
  style?: React.CSSProperties
  /** Geometry of the playing video (see FittedVideo); null while there is none. */
  onVideoLayout?: (layout: VideoLayout | null) => void
}) => {
  const { mediaStore } = useContext(RtcContext)
  const { styleProps, rtcProps } = useContext(PropsContext)
  const { maxViewStyles, videoMode, maxViewOverlayContainer } = styleProps || {}
  const renderModeProp = videoMode?.max
  const [isShown, setIsShown] = useState(false)
  const { user } = props
  // Computed before the hasVideo branch: inside its else-branch the library's (older) bundled
  // TypeScript narrows `user` to `never`.
  const remoteName = styleProps?.remoteName?.trim()
  const placeholderCaption =
    user.uid === 0
      ? 'Your camera is off'
      : remoteName
      ? `${remoteName}'s camera is off`
      : 'Camera is off'

  return (
    <div
      style={{
        ...styles.container,
        ...props.style,
        ...maxViewStyles
      }}
      onMouseEnter={() => setIsShown(true)}
      onMouseLeave={() => setIsShown(false)}
    >
      {user.hasVideo === 1 ? (
        // hasVideo is 1 if the local user has video enabled, or if remote user video is subbed
        <div style={styles.videoContainer}>
          {!rtcProps.disableRtm && <Username user={user} />}
          <Badge user={user} isMaxView={true} />
          <FittedVideo
            fit={renderModeProp || 'cover'}
            videoTrack={mediaStore[user.uid].videoTrack as IRemoteVideoTrack}
            onLayout={props.onVideoLayout}
          />
          {isShown && (
            <div
              style={{
                ...styles.overlay,
                ...maxViewOverlayContainer
              }}
            >
              {!rtcProps.disableRtm && <RemoteVideoMute UIKitUser={user} />}
              {!rtcProps.disableRtm && <RemoteAudioMute UIKitUser={user} />}
            </div>
          )}
        </div>
      ) : (
        <div style={styles.videoContainer}>
          {!rtcProps.disableRtm && <Username user={user} />}
          <Badge user={user} isMaxView={true} />
          <VideoPlaceholder user={user} isShown={isShown} showButtons />
          {/* Say WHY there is no picture, instead of leaving a bare icon that reads as broken. */}
          <div style={styles.placeholderCaption}>{placeholderCaption}</div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { display: 'flex', flex: 1 },
  videoContainer: {
    display: 'flex',
    flex: 1,
    position: 'relative'
  } as CSSProperties,
  overlay: {
    position: 'absolute',
    margin: 5,
    flexDirection: 'column',
    display: 'flex'
  } as CSSProperties,
  placeholderCaption: {
    position: 'absolute',
    top: 'calc(50% + 64px)',
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#cbd5e1',
    fontSize: 14,
    fontWeight: 500,
    pointerEvents: 'none',
    zIndex: 2
  } as CSSProperties,
  username: {
    position: 'absolute',
    background: '#007bffaa',
    padding: '2px 8px',
    color: '#fff',
    margin: 0,
    bottom: 0,
    right: 0,
    zIndex: 90
  } as CSSProperties
}

export default MaxVideoView
